import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { LogOut, Plus, Trash2, Check, X, Edit } from "lucide-react";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Separator } from "@/components/ui/separator";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function getConfig() {
  return { headers: { Authorization: `Bearer ${localStorage.getItem("admin_token")}` } };
}

export default function AdminDashboardPage() {
  const navigate = useNavigate();
  const [stats, setStats] = useState({});
  const [events, setEvents] = useState([]);
  const [journals, setJournals] = useState([]);
  const [videos, setVideos] = useState([]);
  const [dialogues, setDialogues] = useState([]);
  const [contacts, setContacts] = useState([]);

  const [eventDialog, setEventDialog] = useState(false);
  const [journalDialog, setJournalDialog] = useState(false);
  const [videoDialog, setVideoDialog] = useState(false);
  const [editingEvent, setEditingEvent] = useState(null);
  const [editingJournal, setEditingJournal] = useState(null);

  const emptyEvent = { title: "", slug: "", featured_image: "", short_description: "", full_description: "", event_date: "", event_end_date: "", event_type: "Dialogue", organized_by: "", location: "", contribution_type: "Free", registration_link: "", contact_info: "bikram@jdf.org.in", gallery: [], recording_link: "", who_is_this_for: "", what_will_be_explored: "", schedule: "" };
  const emptyJournal = { title: "", slug: "", featured_image: "", excerpt: "", content: "", tags: [], category: "", substack_url: "", is_draft: false };
  const emptyVideo = { youtube_video_id: "", title: "", thumbnail: "", description: "" };

  const [eventForm, setEventForm] = useState(emptyEvent);
  const [journalForm, setJournalForm] = useState(emptyJournal);
  const [videoForm, setVideoForm] = useState(emptyVideo);
  const [tagsInput, setTagsInput] = useState("");

  const load = useCallback(async () => {
    try {
      const c = getConfig();
      const [s, e, j, v, d, co] = await Promise.all([
        axios.get(`${API}/admin/stats`, c),
        axios.get(`${API}/admin/events`, c),
        axios.get(`${API}/admin/journals`, c),
        axios.get(`${API}/admin/videos`, c),
        axios.get(`${API}/admin/dialogue-requests`, c),
        axios.get(`${API}/admin/contacts`, c),
      ]);
      setStats(s.data); setEvents(e.data); setJournals(j.data); setVideos(v.data); setDialogues(d.data); setContacts(co.data);
    } catch {
      toast.error("Session expired. Please login again.");
      navigate("/admin/login");
    }
  }, [navigate]);

  useEffect(() => {
    if (!localStorage.getItem("admin_token")) { navigate("/admin/login"); return; }
    load();
  }, [load, navigate]);

  const logout = () => { localStorage.removeItem("admin_token"); localStorage.removeItem("admin_user"); navigate("/admin/login"); };

  // Event CRUD
  const saveEvent = async () => {
    try {
      const c = getConfig();
      if (editingEvent) {
        await axios.put(`${API}/admin/events/${editingEvent}`, eventForm, c);
        toast.success("Event updated.");
      } else {
        await axios.post(`${API}/admin/events`, eventForm, c);
        toast.success("Event created.");
      }
      setEventDialog(false); setEditingEvent(null); setEventForm(emptyEvent); load();
    } catch (err) { toast.error(err.response?.data?.detail || "Failed to save event."); }
  };

  const deleteEvent = async (id) => {
    if (!window.confirm("Delete this event?")) return;
    try { await axios.delete(`${API}/admin/events/${id}`, getConfig()); toast.success("Deleted."); load(); } catch { toast.error("Delete failed."); }
  };

  const editEvent = (ev) => { setEditingEvent(ev.id); setEventForm(ev); setEventDialog(true); };

  // Journal CRUD
  const saveJournal = async () => {
    const data = { ...journalForm, tags: tagsInput.split(",").map((t) => t.trim()).filter(Boolean) };
    try {
      const c = getConfig();
      if (editingJournal) {
        await axios.put(`${API}/admin/journals/${editingJournal}`, data, c);
        toast.success("Journal updated.");
      } else {
        await axios.post(`${API}/admin/journals`, data, c);
        toast.success("Journal created.");
      }
      setJournalDialog(false); setEditingJournal(null); setJournalForm(emptyJournal); setTagsInput(""); load();
    } catch (err) { toast.error(err.response?.data?.detail || "Failed to save journal."); }
  };

  const deleteJournal = async (id) => {
    if (!window.confirm("Delete this journal?")) return;
    try { await axios.delete(`${API}/admin/journals/${id}`, getConfig()); toast.success("Deleted."); load(); } catch { toast.error("Delete failed."); }
  };

  const editJournal = (j) => { setEditingJournal(j.id); setJournalForm(j); setTagsInput(j.tags?.join(", ") || ""); setJournalDialog(true); };

  // Video CRUD
  const saveVideo = async () => {
    try {
      await axios.post(`${API}/admin/videos`, videoForm, getConfig());
      toast.success("Video added."); setVideoDialog(false); setVideoForm(emptyVideo); load();
    } catch (err) { toast.error(err.response?.data?.detail || "Failed to add video."); }
  };

  const deleteVideo = async (id) => {
    if (!window.confirm("Delete this video?")) return;
    try { await axios.delete(`${API}/admin/videos/${id}`, getConfig()); toast.success("Deleted."); load(); } catch { toast.error("Delete failed."); }
  };

  // Dialogue status
  const updateDialogueStatus = async (id, status) => {
    try { await axios.patch(`${API}/admin/dialogue-requests/${id}`, { status }, getConfig()); toast.success(`Marked as ${status}.`); load(); } catch { toast.error("Failed to update."); }
  };

  const inputCls = "bg-transparent border border-[hsl(48,12%,88%)] rounded-none px-3 py-2 font-body text-sm focus-visible:ring-0 focus-visible:border-[#5C6B5D]";
  const textareaCls = `${inputCls} min-h-[80px] resize-none`;

  return (
    <div className="min-h-screen" style={{ backgroundColor: "#FDFCF8" }} data-testid="admin-dashboard">
      {/* Header */}
      <header className="border-b border-[hsl(48,12%,88%)] px-6 md:px-12 py-4 flex items-center justify-between">
        <h1 className="font-heading text-xl text-foreground">Admin Dashboard</h1>
        <button onClick={logout} className="flex items-center gap-2 font-body text-sm text-muted-foreground hover:text-foreground transition-colors" data-testid="admin-logout">
          <LogOut size={16} strokeWidth={1.5} /> Logout
        </button>
      </header>

      <div className="max-w-screen-xl mx-auto px-6 md:px-12 py-8">
        {/* Stats */}
        <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-8">
          {[
            { label: "Events", val: stats.events },
            { label: "Journals", val: stats.journals },
            { label: "Videos", val: stats.videos },
            { label: "Dialogues", val: stats.dialogue_requests },
            { label: "Messages", val: stats.contacts },
          ].map((s) => (
            <div key={s.label} className="border border-[hsl(48,12%,88%)] p-4" data-testid={`stat-${s.label.toLowerCase()}`}>
              <p className="font-body text-xs tracking-wider uppercase text-muted-foreground">{s.label}</p>
              <p className="font-heading text-2xl text-foreground mt-1">{s.val ?? "—"}</p>
            </div>
          ))}
        </div>

        {/* Tabs */}
        <Tabs defaultValue="events">
          <TabsList className="bg-transparent border-b border-[hsl(48,12%,88%)] rounded-none w-full justify-start gap-0 h-auto p-0">
            {["events", "journals", "videos", "dialogues", "messages"].map((t) => (
              <TabsTrigger key={t} value={t} className="font-body text-sm tracking-wide capitalize rounded-none border-b-2 border-transparent data-[state=active]:border-[#5C6B5D] data-[state=active]:text-foreground text-muted-foreground px-4 py-3 data-[state=active]:shadow-none bg-transparent" data-testid={`tab-${t}`}>
                {t}
              </TabsTrigger>
            ))}
          </TabsList>

          {/* Events Tab */}
          <TabsContent value="events" className="mt-6">
            <div className="flex justify-between items-center mb-6">
              <h2 className="font-heading text-xl text-foreground">Events</h2>
              <button onClick={() => { setEditingEvent(null); setEventForm(emptyEvent); setEventDialog(true); }} className="flex items-center gap-1.5 font-body text-sm text-[#5C6B5D] hover:underline" data-testid="add-event-btn"><Plus size={16} /> Add Event</button>
            </div>
            <div className="space-y-3">
              {events.map((ev) => (
                <div key={ev.id} className="flex items-center justify-between border border-[hsl(48,12%,88%)] p-4" data-testid={`admin-event-${ev.id}`}>
                  <div className="flex-1 min-w-0">
                    <p className="font-body text-sm font-medium text-foreground truncate">{ev.title}</p>
                    <p className="font-body text-xs text-muted-foreground">{ev.event_date} — {ev.event_type} — {ev.location}</p>
                  </div>
                  <div className="flex gap-2 ml-4">
                    <button onClick={() => editEvent(ev)} className="p-1.5 text-muted-foreground hover:text-foreground transition-colors"><Edit size={14} strokeWidth={1.5} /></button>
                    <button onClick={() => deleteEvent(ev.id)} className="p-1.5 text-muted-foreground hover:text-red-600 transition-colors"><Trash2 size={14} strokeWidth={1.5} /></button>
                  </div>
                </div>
              ))}
              {events.length === 0 && <p className="font-body text-sm text-muted-foreground italic">No events yet.</p>}
            </div>
          </TabsContent>

          {/* Journals Tab */}
          <TabsContent value="journals" className="mt-6">
            <div className="flex justify-between items-center mb-6">
              <h2 className="font-heading text-xl text-foreground">Journals</h2>
              <button onClick={() => { setEditingJournal(null); setJournalForm(emptyJournal); setTagsInput(""); setJournalDialog(true); }} className="flex items-center gap-1.5 font-body text-sm text-[#5C6B5D] hover:underline" data-testid="add-journal-btn"><Plus size={16} /> Add Journal</button>
            </div>
            <div className="space-y-3">
              {journals.map((j) => (
                <div key={j.id} className="flex items-center justify-between border border-[hsl(48,12%,88%)] p-4" data-testid={`admin-journal-${j.id}`}>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <p className="font-body text-sm font-medium text-foreground truncate">{j.title}</p>
                      {j.is_draft && <Badge variant="outline" className="text-[10px] tracking-wider uppercase border-yellow-500 text-yellow-600">Draft</Badge>}
                    </div>
                    <p className="font-body text-xs text-muted-foreground">{j.publish_date?.slice(0, 10)} — {j.category}</p>
                  </div>
                  <div className="flex gap-2 ml-4">
                    <button onClick={() => editJournal(j)} className="p-1.5 text-muted-foreground hover:text-foreground transition-colors"><Edit size={14} strokeWidth={1.5} /></button>
                    <button onClick={() => deleteJournal(j.id)} className="p-1.5 text-muted-foreground hover:text-red-600 transition-colors"><Trash2 size={14} strokeWidth={1.5} /></button>
                  </div>
                </div>
              ))}
              {journals.length === 0 && <p className="font-body text-sm text-muted-foreground italic">No journals yet.</p>}
            </div>
          </TabsContent>

          {/* Videos Tab */}
          <TabsContent value="videos" className="mt-6">
            <div className="flex justify-between items-center mb-6">
              <h2 className="font-heading text-xl text-foreground">Videos</h2>
              <button onClick={() => { setVideoForm(emptyVideo); setVideoDialog(true); }} className="flex items-center gap-1.5 font-body text-sm text-[#5C6B5D] hover:underline" data-testid="add-video-btn"><Plus size={16} /> Add Video</button>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {videos.map((v) => (
                <div key={v.id} className="border border-[hsl(48,12%,88%)] p-4" data-testid={`admin-video-${v.id}`}>
                  <img src={v.thumbnail || `https://img.youtube.com/vi/${v.youtube_video_id}/mqdefault.jpg`} alt={v.title} className="w-full aspect-video object-cover mb-3" />
                  <p className="font-body text-sm font-medium text-foreground truncate">{v.title}</p>
                  <p className="font-body text-xs text-muted-foreground mt-1">ID: {v.youtube_video_id}</p>
                  <button onClick={() => deleteVideo(v.id)} className="mt-2 flex items-center gap-1 font-body text-xs text-muted-foreground hover:text-red-600 transition-colors"><Trash2 size={12} /> Remove</button>
                </div>
              ))}
              {videos.length === 0 && <p className="font-body text-sm text-muted-foreground italic col-span-3">No videos yet. Add YouTube video IDs to display on the site.</p>}
            </div>
          </TabsContent>

          {/* Dialogues Tab */}
          <TabsContent value="dialogues" className="mt-6">
            <h2 className="font-heading text-xl text-foreground mb-6">Dialogue Requests</h2>
            <div className="space-y-3">
              {dialogues.map((d) => (
                <div key={d.id} className="border border-[hsl(48,12%,88%)] p-4" data-testid={`admin-dialogue-${d.id}`}>
                  <div className="flex items-start justify-between gap-4">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-1">
                        <p className="font-body text-sm font-medium text-foreground">{d.name}</p>
                        <Badge variant="outline" className={`text-[10px] tracking-wider uppercase ${d.status === "approved" ? "border-green-600 text-green-600" : d.status === "rejected" ? "border-red-600 text-red-600" : "border-[hsl(48,12%,88%)] text-muted-foreground"}`}>
                          {d.status}
                        </Badge>
                      </div>
                      <p className="font-body text-xs text-muted-foreground">{d.email} — {d.city}</p>
                      <p className="font-body text-sm text-muted-foreground mt-2 line-clamp-2">{d.reason}</p>
                      {d.preferred_time && <p className="font-body text-xs text-muted-foreground mt-1">Preferred: {d.preferred_time}</p>}
                      <p className="font-body text-xs text-muted-foreground/50 mt-1">{d.created_at?.slice(0, 10)}</p>
                    </div>
                    {d.status === "pending" && (
                      <div className="flex gap-1">
                        <button onClick={() => updateDialogueStatus(d.id, "approved")} className="p-1.5 text-green-600 hover:bg-green-50 transition-colors" title="Approve"><Check size={16} /></button>
                        <button onClick={() => updateDialogueStatus(d.id, "rejected")} className="p-1.5 text-red-600 hover:bg-red-50 transition-colors" title="Reject"><X size={16} /></button>
                      </div>
                    )}
                  </div>
                </div>
              ))}
              {dialogues.length === 0 && <p className="font-body text-sm text-muted-foreground italic">No dialogue requests yet.</p>}
            </div>
          </TabsContent>

          {/* Messages Tab */}
          <TabsContent value="messages" className="mt-6">
            <h2 className="font-heading text-xl text-foreground mb-6">Contact Messages</h2>
            <div className="space-y-3">
              {contacts.map((c) => (
                <div key={c.id} className="border border-[hsl(48,12%,88%)] p-4" data-testid={`admin-contact-${c.id}`}>
                  <div className="flex items-center gap-2 mb-1">
                    <p className="font-body text-sm font-medium text-foreground">{c.name}</p>
                    <Badge variant="outline" className="text-[10px] tracking-wider uppercase border-[hsl(48,12%,88%)] text-muted-foreground">{c.contact_type}</Badge>
                  </div>
                  <p className="font-body text-xs text-muted-foreground">{c.email}</p>
                  <p className="font-body text-sm text-muted-foreground mt-2">{c.message}</p>
                  <p className="font-body text-xs text-muted-foreground/50 mt-1">{c.created_at?.slice(0, 10)}</p>
                </div>
              ))}
              {contacts.length === 0 && <p className="font-body text-sm text-muted-foreground italic">No messages yet.</p>}
            </div>
          </TabsContent>
        </Tabs>
      </div>

      {/* Event Dialog */}
      <Dialog open={eventDialog} onOpenChange={setEventDialog}>
        <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto bg-[#FDFCF8] border-[hsl(48,12%,88%)] rounded-none">
          <DialogHeader><DialogTitle className="font-heading text-xl">{editingEvent ? "Edit Event" : "New Event"}</DialogTitle><DialogDescription className="font-body text-sm text-muted-foreground">Fill in the event details below.</DialogDescription></DialogHeader>
          <div className="space-y-4 mt-4">
            <div className="grid grid-cols-2 gap-4">
              <div><Label className="font-body text-xs">Title *</Label><Input value={eventForm.title} onChange={(e) => setEventForm({ ...eventForm, title: e.target.value })} className={inputCls} data-testid="event-form-title" /></div>
              <div><Label className="font-body text-xs">Slug *</Label><Input value={eventForm.slug} onChange={(e) => setEventForm({ ...eventForm, slug: e.target.value })} className={inputCls} data-testid="event-form-slug" /></div>
            </div>
            <div><Label className="font-body text-xs">Featured Image URL</Label><Input value={eventForm.featured_image} onChange={(e) => setEventForm({ ...eventForm, featured_image: e.target.value })} className={inputCls} /></div>
            <div><Label className="font-body text-xs">Short Description</Label><Textarea value={eventForm.short_description} onChange={(e) => setEventForm({ ...eventForm, short_description: e.target.value })} className={textareaCls} /></div>
            <div><Label className="font-body text-xs">Full Description</Label><Textarea value={eventForm.full_description} onChange={(e) => setEventForm({ ...eventForm, full_description: e.target.value })} className={`${textareaCls} min-h-[120px]`} /></div>
            <div className="grid grid-cols-2 gap-4">
              <div><Label className="font-body text-xs">Event Date *</Label><Input type="date" value={eventForm.event_date} onChange={(e) => setEventForm({ ...eventForm, event_date: e.target.value })} className={inputCls} data-testid="event-form-date" /></div>
              <div><Label className="font-body text-xs">End Date</Label><Input type="date" value={eventForm.event_end_date} onChange={(e) => setEventForm({ ...eventForm, event_end_date: e.target.value })} className={inputCls} /></div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label className="font-body text-xs">Event Type</Label>
                <Select value={eventForm.event_type} onValueChange={(v) => setEventForm({ ...eventForm, event_type: v })}>
                  <SelectTrigger className={inputCls} data-testid="event-form-type"><SelectValue /></SelectTrigger>
                  <SelectContent className="bg-[#FDFCF8] border-[hsl(48,12%,88%)] rounded-none">
                    {["Dialogue", "Retreat", "Climate Workshops", "Education Programs", "Village Immersion"].map((t) => <SelectItem key={t} value={t} className="font-body text-sm">{t}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="font-body text-xs">Contribution Type</Label>
                <Select value={eventForm.contribution_type} onValueChange={(v) => setEventForm({ ...eventForm, contribution_type: v })}>
                  <SelectTrigger className={inputCls}><SelectValue /></SelectTrigger>
                  <SelectContent className="bg-[#FDFCF8] border-[hsl(48,12%,88%)] rounded-none">
                    {["Free", "Contribution-based", "Paid"].map((t) => <SelectItem key={t} value={t} className="font-body text-sm">{t}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div><Label className="font-body text-xs">Organized By</Label><Input value={eventForm.organized_by} onChange={(e) => setEventForm({ ...eventForm, organized_by: e.target.value })} className={inputCls} /></div>
              <div><Label className="font-body text-xs">Location</Label><Input value={eventForm.location} onChange={(e) => setEventForm({ ...eventForm, location: e.target.value })} className={inputCls} /></div>
            </div>
            <div><Label className="font-body text-xs">Registration Link</Label><Input value={eventForm.registration_link} onChange={(e) => setEventForm({ ...eventForm, registration_link: e.target.value })} className={inputCls} /></div>
            <div><Label className="font-body text-xs">Contact Info</Label><Input value={eventForm.contact_info} onChange={(e) => setEventForm({ ...eventForm, contact_info: e.target.value })} className={inputCls} /></div>
            <div><Label className="font-body text-xs">Who Is This For</Label><Textarea value={eventForm.who_is_this_for} onChange={(e) => setEventForm({ ...eventForm, who_is_this_for: e.target.value })} className={textareaCls} /></div>
            <div><Label className="font-body text-xs">What Will Be Explored</Label><Textarea value={eventForm.what_will_be_explored} onChange={(e) => setEventForm({ ...eventForm, what_will_be_explored: e.target.value })} className={textareaCls} /></div>
            <div><Label className="font-body text-xs">Schedule</Label><Textarea value={eventForm.schedule} onChange={(e) => setEventForm({ ...eventForm, schedule: e.target.value })} className={textareaCls} /></div>
            <Separator style={{ backgroundColor: "#E5E4DE" }} />
            <button onClick={saveEvent} className="font-body text-sm tracking-[0.15em] uppercase px-8 py-3 bg-[#5C6B5D] text-white hover:bg-[#5C6B5D]/90 transition-all duration-300" data-testid="event-form-save">
              {editingEvent ? "Update Event" : "Create Event"}
            </button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Journal Dialog */}
      <Dialog open={journalDialog} onOpenChange={setJournalDialog}>
        <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto bg-[#FDFCF8] border-[hsl(48,12%,88%)] rounded-none">
          <DialogHeader><DialogTitle className="font-heading text-xl">{editingJournal ? "Edit Journal" : "New Journal"}</DialogTitle><DialogDescription className="font-body text-sm text-muted-foreground">Fill in the journal details below.</DialogDescription></DialogHeader>
          <div className="space-y-4 mt-4">
            <div className="grid grid-cols-2 gap-4">
              <div><Label className="font-body text-xs">Title *</Label><Input value={journalForm.title} onChange={(e) => setJournalForm({ ...journalForm, title: e.target.value })} className={inputCls} data-testid="journal-form-title" /></div>
              <div><Label className="font-body text-xs">Slug *</Label><Input value={journalForm.slug} onChange={(e) => setJournalForm({ ...journalForm, slug: e.target.value })} className={inputCls} data-testid="journal-form-slug" /></div>
            </div>
            <div><Label className="font-body text-xs">Featured Image URL</Label><Input value={journalForm.featured_image} onChange={(e) => setJournalForm({ ...journalForm, featured_image: e.target.value })} className={inputCls} /></div>
            <div><Label className="font-body text-xs">Excerpt</Label><Textarea value={journalForm.excerpt} onChange={(e) => setJournalForm({ ...journalForm, excerpt: e.target.value })} className={textareaCls} /></div>
            <div><Label className="font-body text-xs">Content *</Label><Textarea value={journalForm.content} onChange={(e) => setJournalForm({ ...journalForm, content: e.target.value })} className={`${textareaCls} min-h-[200px]`} data-testid="journal-form-content" /></div>
            <div className="grid grid-cols-2 gap-4">
              <div><Label className="font-body text-xs">Tags (comma-separated)</Label><Input value={tagsInput} onChange={(e) => setTagsInput(e.target.value)} className={inputCls} placeholder="inquiry, awareness" /></div>
              <div><Label className="font-body text-xs">Category</Label><Input value={journalForm.category} onChange={(e) => setJournalForm({ ...journalForm, category: e.target.value })} className={inputCls} /></div>
            </div>
            <div><Label className="font-body text-xs">Substack URL</Label><Input value={journalForm.substack_url} onChange={(e) => setJournalForm({ ...journalForm, substack_url: e.target.value })} className={inputCls} /></div>
            <div className="flex items-center gap-3 pt-2">
              <input 
                type="checkbox" 
                id="is_draft" 
                checked={journalForm.is_draft || false} 
                onChange={(e) => setJournalForm({ ...journalForm, is_draft: e.target.checked })} 
                className="w-4 h-4 rounded border-[hsl(48,12%,88%)] text-[#5C6B5D] focus:ring-[#5C6B5D]"
                data-testid="journal-form-draft"
              />
              <Label htmlFor="is_draft" className="font-body text-sm text-muted-foreground cursor-pointer">Save as draft (won't be published publicly)</Label>
            </div>
            <Separator style={{ backgroundColor: "#E5E4DE" }} />
            <div className="flex gap-3">
              <button onClick={saveJournal} className="font-body text-sm tracking-[0.15em] uppercase px-8 py-3 bg-[#5C6B5D] text-white hover:bg-[#5C6B5D]/90 transition-all duration-300" data-testid="journal-form-save">
                {editingJournal ? "Update Journal" : journalForm.is_draft ? "Save Draft" : "Publish Journal"}
              </button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Video Dialog */}
      <Dialog open={videoDialog} onOpenChange={setVideoDialog}>
        <DialogContent className="max-w-md bg-[#FDFCF8] border-[hsl(48,12%,88%)] rounded-none">
          <DialogHeader><DialogTitle className="font-heading text-xl">Add Video</DialogTitle><DialogDescription className="font-body text-sm text-muted-foreground">Enter the YouTube video ID and title.</DialogDescription></DialogHeader>
          <div className="space-y-4 mt-4">
            <div><Label className="font-body text-xs">YouTube Video ID *</Label><Input value={videoForm.youtube_video_id} onChange={(e) => setVideoForm({ ...videoForm, youtube_video_id: e.target.value })} className={inputCls} placeholder="e.g., dQw4w9WgXcQ" data-testid="video-form-id" /></div>
            <div><Label className="font-body text-xs">Title *</Label><Input value={videoForm.title} onChange={(e) => setVideoForm({ ...videoForm, title: e.target.value })} className={inputCls} data-testid="video-form-title" /></div>
            <div><Label className="font-body text-xs">Description</Label><Textarea value={videoForm.description} onChange={(e) => setVideoForm({ ...videoForm, description: e.target.value })} className={textareaCls} /></div>
            <Separator style={{ backgroundColor: "#E5E4DE" }} />
            <button onClick={saveVideo} className="font-body text-sm tracking-[0.15em] uppercase px-8 py-3 bg-[#5C6B5D] text-white hover:bg-[#5C6B5D]/90 transition-all duration-300" data-testid="video-form-save">
              Add Video
            </button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
